/**
 * Visit Schedule Page (訪問入力 - Recepro A / Recepro R).
 * Displays massager selector, visit date, and week calendar. New: list from API. Create/Edit/Copy: draft local, データ更新 to submit.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/shared/providers/AuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  useVisitScheduleDayDetail,
  useVisitScheduleMassagers,
  useVisitScheduleVisits,
  useVisitScheduleWeekCheckMutation,
  useVisitScheduleWeekHasDataMutation,
  useVisitScheduleSubmitTotalMutation,
  useVisitScheduleTreatmentBatchCheckMutation,
} from '@/hooks/main/useVisitSchedule';
import type { VisitScheduleVisitDisplay } from './components/VisitScheduleWeekCalendar';
import VisitScheduleModal from './components/VisitScheduleModal';
import { VisitScheduleCopyDayModal } from './components/VisitScheduleCopyDayModal';
import { VisitScheduleDetailModal } from './components/VisitScheduleDetailModal';
import { useAppToast } from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { usePrintWeeklyScheduleMutation } from '@hooks/main/usePrintWeeklyScheduleMutation';
import dayjs from 'dayjs';
import {
  VisitScheduleCalendarPanel,
  VisitScheduleEmptyState,
  VisitScheduleLoading,
  VisitScheduleSubmitBar,
  VisitScheduleToolbar,
} from './components/VisitSchedulePageSections';
import { useVisitSchedulePageDraftActions } from './components/useVisitSchedulePageDraftActions';
import { MSG, VISITDATE_DEFAULT } from './VisitSchedulePage.constants';
import {
  addUpdateDates,
  buildMassagerOptions,
  getDeparturePositionText,
  getMassagerSerialSortValue,
  getWeekRangeForVisitdate,
  isMassagerAvailableOnVisitDate,
  nextDraftId,
  weekDatesOf,
} from './VisitSchedulePage.helpers';

export default function VisitSchedulePage() {
  const navigate = useNavigate();
  const {
    tenant,
    massagerId: massagerIdParam,
    clinicLocationId: clinicLocationIdParam,
    visitdate: visitdateParam,
  } = useParams<{
    tenant: string;
    massagerId: string;
    clinicLocationId?: string;
    visitdate: string;
  }>();
  const { clinic } = useAuth();
  const { serviceType } = useTheme();
  const toast = useAppToast();
  const appModal = useAppModal();
  const { triggerPrint, isLoading: isPrinting } = usePrintWeeklyScheduleMutation();
  // Canonical URL: /visits/schedule/{massagerId}/{clinicLocationId}/{visitdate}.
  // Legacy 2-segment URL (cross-link without location) → urlClinicLocationId = null,
  // page picks first dropdown entry of that massager and rewrites URL.
  const parsedMassagerId = massagerIdParam ? Number(massagerIdParam) : null;
  const urlMassagerId =
    parsedMassagerId && Number.isFinite(parsedMassagerId) ? parsedMassagerId : null;
  const parsedClinicLocationId = clinicLocationIdParam ? Number(clinicLocationIdParam) : null;
  const urlClinicLocationId =
    parsedClinicLocationId && Number.isFinite(parsedClinicLocationId)
      ? parsedClinicLocationId
      : null;
  const visitdate =
    visitdateParam && dayjs(visitdateParam, 'YYYY-MM-DD', true).isValid()
      ? visitdateParam
      : VISITDATE_DEFAULT;
  const [isFullMonth, setIsFullMonth] = useState(false);
  /** Copy-day modal: source date when open */
  const [copyDayModalFromDate, setCopyDayModalFromDate] = useState<string | null>(null);
  const [detailModalDate, setDetailModalDate] = useState<string | null>(null);
  /** RA2: after copy (day/week) without submit, block delete/edit/add until データ更新 */
  const [copyCheckFlg, setCopyCheckFlg] = useState(false);
  /** RA2: copy_memo — 次週にコピー時「メモを含む」チェックで massager_remarks をコピーする */
  const [copyWeekIncludeMemo, setCopyWeekIncludeMemo] = useState(false);
  const [isCopyWeekSubmitting, setIsCopyWeekSubmitting] = useState(false);
  const checkinEnabled = Boolean(clinic?.is_enable_checkin);
  const isCheckinEnabledForVisit = useCallback(
    (visit: VisitScheduleVisitDisplay): boolean => {
      if (!checkinEnabled) return false;

      const visitDate = dayjs(visit.visit_datetime_from);
      if (!visitDate.isValid()) return false;

      const fromRaw = clinic?.checkin_available_from;
      if (fromRaw) {
        const from = dayjs(fromRaw).startOf('day');
        if (from.isValid() && visitDate.isBefore(from, 'day')) return false;
      }

      const toRaw = clinic?.checkin_available_to;
      if (toRaw) {
        const to = dayjs(toRaw).startOf('day');
        if (to.isValid() && visitDate.isAfter(to, 'day')) return false;
      }

      return true;
    },
    [checkinEnabled, clinic?.checkin_available_from, clinic?.checkin_available_to]
  );

  const isR2 = serviceType === SERVICE_TYPE_R2;
  const massagerLabel = isR2 ? 'マッサージ師' : '鍼灸師';

  const { massagers, isLoading: loadingMassagers } = useVisitScheduleMassagers(visitdate);

  // Filter+sort CLM rows for the dropdown:
  //   filter: visitdate ∈ [available_from, available_to]
  //   sort:   massager.serial_number ASC, then CLM.id ASC
  const massagersList = useMemo(() => {
    const list = Array.isArray(massagers) ? massagers : [];
    return [...list]
      .filter(r => isMassagerAvailableOnVisitDate(r, visitdate))
      .sort((a, b) => {
        const serialCompare =
          getMassagerSerialSortValue(a.massager?.serial_number) -
          getMassagerSerialSortValue(b.massager?.serial_number);
        if (serialCompare !== 0) return serialCompare;
        return a.id - b.id;
      });
  }, [massagers, visitdate]);

  // Resolve dropdown row by (massager_id, clinic_location_id) — pair stable across
  // admin edits (CLM id can change but the underlying massager/location pair doesn't).
  // If URL only carries massager_id (legacy/cross-link), pick first row of that massager.
  const selectedRow = useMemo(() => {
    if (urlMassagerId === null) return null;
    if (urlClinicLocationId !== null) {
      return (
        massagersList.find(
          r => r.massager?.id === urlMassagerId && r.clinic_location?.id === urlClinicLocationId
        ) ?? null
      );
    }
    return massagersList.find(r => r.massager?.id === urlMassagerId) ?? null;
  }, [massagersList, urlMassagerId, urlClinicLocationId]);
  const massagerId = selectedRow?.massager?.id ?? null;
  const clinicLocationId = selectedRow?.clinic_location?.id ?? null;
  const departurePositionText = useMemo(
    () => getDeparturePositionText(selectedRow),
    [selectedRow]
  );

  const {
    visits,
    data: visitsResponse,
    isLoading: loadingVisits,
    isFetching: isFetchingVisits,
    refetch: refetchVisits,
  } = useVisitScheduleVisits(massagerId, visitdate, clinicLocationId);
  const { detail: dayDetail, isLoading: loadingDayDetail } = useVisitScheduleDayDetail(
    detailModalDate ? massagerId : null,
    detailModalDate,
    detailModalDate ? clinicLocationId : null
  );
  const { checkWeekMutation, isLoading: isCheckingNextWeek } = useVisitScheduleWeekCheckMutation();
  const { checkWeekHasDataMutation } = useVisitScheduleWeekHasDataMutation();
  const { checkTreatmentBatchMutation } = useVisitScheduleTreatmentBatchCheckMutation();
  const { submitTotalMutation, isLoading: isSubmitting } = useVisitScheduleSubmitTotalMutation();

  const [calendarVisits, setCalendarVisits] = useState<VisitScheduleVisitDisplay[]>([]);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  /**
   * Days this session actually changed, and days it has fully loaded.
   *
   * Only their intersection is sent as `update_dates`, which is the ONLY scope the server
   * may delete rows in. A day the user edited without the calendar ever loading it (the
   * modal's 訪問日 accepts any date) still gets its visit saved, but authorises no deletion —
   * the browser cannot know what else that day holds.
   */
  const [updateDates, setUpdateDates] = useState<string[]>([]);
  const [loadedDates, setLoadedDates] = useState<string[]>([]);
  const [isAwaitingSubmitRefresh, setIsAwaitingSubmitRefresh] = useState(false);
  const hasLocalChangesRef = useRef(false);
  const submitRefreshStartedRef = useRef(false);
  const copyDaySubmitLockRef = useRef(false);
  const copyWeekSubmitLockRef = useRef(false);

  // Service type switch (A2 ↔ R2): discard any local draft state so unsaved edits
  // from the previous service do not leak into the new service's calendar.
  useEffect(() => {
    setCalendarVisits([]);
    setDeletedIds([]);
    setUpdateDates([]);
    setLoadedDates([]);
    hasLocalChangesRef.current = false;
  }, [serviceType]);

  /**
   * A day may only authorise a deletion once its whole week has come back from the server.
   * Reset on every (massager, location) change: days loaded for one pair say nothing about
   * what another pair has on them.
   */
  useEffect(() => {
    setLoadedDates([]);
  }, [massagerId, clinicLocationId]);

  useEffect(() => {
    // `visitsResponse` is keyed on (massager, location, visitdate), so it stays undefined
    // until THIS week's rows are in hand — a still-loading week must not be marked loaded.
    if (!visitsResponse || isFetchingVisits) return;
    setLoadedDates(prev => addUpdateDates(prev, ...weekDatesOf(visitdate)));
  }, [visitsResponse, isFetchingVisits, visitdate]);

  const markDatesDirty = useCallback((...dates: (string | null | undefined)[]) => {
    setUpdateDates(prev => addUpdateDates(prev, ...dates));
  }, []);

  /**
   * GAP-S1 — port of A2 addUpdateDatesForPatient (schedule.blade.php:220-233).
   *
   * Touching one visit makes every OTHER day of that same patient dirty too: consent and
   * coverage-derived flags, 持ち回り grouping and 明細書発行加算 are all evaluated per
   * patient, so leaving the patient's other days out of the payload leaves them stale.
   *
   * Bounded to the week on screen, which is exactly A2's bound — its calendar is
   * `agendaWeek` and the store does `replaceServerRange`, so `clientEvents` only ever
   * holds the displayed week. Weeks browsed earlier are deliberately excluded: this
   * client may be holding rows the server has since changed, and a day in the payload is
   * treated as that day's complete new state. (A2 solves that by re-fetching the whole
   * month in prepareSubmitMonths before submitting — not ported here.)
   */
  const expandDirtyDatesByPatient = useCallback(
    (seedDates: string[]): Set<string> => {
      const expanded = new Set(seedDates);
      const seeds = new Set(seedDates);

      const impactedPatientIds = new Set(
        calendarVisits
          .filter(v => seeds.has(dayjs(v.visit_datetime_from).format('YYYY-MM-DD')))
          .map(v => v.patient_id)
          .filter((id): id is number => typeof id === 'number' && id > 0)
      );
      if (impactedPatientIds.size === 0) return expanded;

      const inScope = new Set(weekDatesOf(visitdate).filter(d => loadedDates.includes(d)));
      calendarVisits.forEach(v => {
        if (typeof v.patient_id !== 'number' || !impactedPatientIds.has(v.patient_id)) return;
        const day = dayjs(v.visit_datetime_from).format('YYYY-MM-DD');
        if (inScope.has(day)) expanded.add(day);
      });

      return expanded;
    },
    [calendarVisits, loadedDates, visitdate]
  );

  const updateScheduleUrl = useCallback(
    (
      nextMassagerId: number | null,
      nextClinicLocationId: number | null,
      nextVisitdate: string,
      replace = false
    ) => {
      if (!tenant) return;
      const normalizedDate =
        nextVisitdate && dayjs(nextVisitdate, 'YYYY-MM-DD', true).isValid()
          ? nextVisitdate
          : VISITDATE_DEFAULT;
      if (!nextMassagerId) {
        navigate(`/${tenant}/visits/schedule`, { replace });
        return;
      }
      if (nextClinicLocationId !== null) {
        navigate(
          `/${tenant}/visits/schedule/${nextMassagerId}/${nextClinicLocationId}/${normalizedDate}`,
          { replace }
        );
        return;
      }
      // Legacy 2-segment fallback (caller has no location info — page picks first).
      navigate(`/${tenant}/visits/schedule/${nextMassagerId}/${normalizedDate}`, { replace });
    },
    [navigate, tenant]
  );

  useEffect(() => {
    if (!hasLocalChangesRef.current) {
      setCalendarVisits(visits as VisitScheduleVisitDisplay[]);
      setDeletedIds(prev => (prev.length === 0 ? prev : []));
      return;
    }

    // Keep unsaved local edits/drafts, but merge in fresh server rows for the newly viewed week.
    // This prevents "week data disappears" when navigating after editing a visit across weeks.
    setCalendarVisits(prev => {
      const localNumericIds = new Set(
        prev
          .map(v => v.id)
          .filter((id): id is number => typeof id === 'number' && !deletedIds.includes(id))
      );
      const mergedServerRows = (visits as VisitScheduleVisitDisplay[]).filter(v => {
        if (typeof v.id !== 'number') return false;
        if (deletedIds.includes(v.id)) return false;
        return !localNumericIds.has(v.id);
      });
      const keptLocalRows = prev.filter(v => {
        if (typeof v.id === 'number') return !deletedIds.includes(v.id);
        return true; // keep drafts
      });
      return [...mergedServerRows, ...keptLocalRows];
    });
  }, [visits, deletedIds]);

  useEffect(() => {
    if (!isAwaitingSubmitRefresh) return;
    if (isFetchingVisits) {
      submitRefreshStartedRef.current = true;
      return;
    }
    if (!submitRefreshStartedRef.current) return;

    setCalendarVisits(visits as VisitScheduleVisitDisplay[]);
    hasLocalChangesRef.current = false;
    setCopyCheckFlg(false);
    setDeletedIds([]);
    setUpdateDates([]);
    setIsAwaitingSubmitRefresh(false);
    submitRefreshStartedRef.current = false;
  }, [isAwaitingSubmitRefresh, isFetchingVisits, visits]);

  const goPrevWeek = useCallback(() => {
    updateScheduleUrl(
      massagerId,
      clinicLocationId,
      dayjs(visitdate).subtract(7, 'day').format('YYYY-MM-DD')
    );
  }, [massagerId, clinicLocationId, updateScheduleUrl, visitdate]);

  const goNextWeek = useCallback(() => {
    updateScheduleUrl(
      massagerId,
      clinicLocationId,
      dayjs(visitdate).add(7, 'day').format('YYYY-MM-DD')
    );
  }, [massagerId, clinicLocationId, updateScheduleUrl, visitdate]);

  const {
    modalOpen,
    setModalOpen,
    selectedVisit,
    initialDate,
    initialTime,
    handleCellClick,
    handleVisitClick,
    handleSave,
    handleCopyToOtherDays,
    handleDelete,
    handleVisitDrop,
    handleVisitResize,
  } = useVisitSchedulePageDraftActions({
    copyCheckFlg,
    tenant: tenant ?? '',
    clinicNumber: clinic?.clinic_number ?? '',
    serviceType,
    massagerId,
    clinicLocationId,
    calendarVisits,
    setCalendarVisits,
    setDeletedIds,
    setCopyCheckFlg,
    hasLocalChangesRef,
    markDatesDirty,
  });

  /**
   * GAP-U1 — port of A2 schedule.blade.php:1992-2018 (R2 :1817-1846).
   *
   * A2/R2 have no push channel; "the visit tab updated itself" is the calendar refetching
   * when the tab comes back to the front, so an edit made in another tab (同意書, 受診者,
   * 住所) is picked up without an F5. RA2N turns `refetchOnWindowFocus` off globally
   * (shared/lib/react-query.ts), so this is wired per-screen instead of flipping that
   * switch for every screen in every portal.
   *
   * Guards mirror A2's: skip while hidden, skip while the visit modal is open (A2 checks
   * `$('#IFrameModalFrame').is(':visible')`), throttle to one refetch per 500ms, and
   * debounce the event by 100ms. The `window focus` listener is R2 parity (R2 :1844-1846)
   * — it also catches switching back from another application, not just another tab.
   *
   * Unsaved drafts survive: the sync effect above merges server rows into local state
   * instead of replacing it whenever hasLocalChangesRef is set.
   */
  const visitTabLastRefreshedAt = useRef(Date.now());
  const visitTabRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const refreshAfterTabActivation = () => {
      if (document.hidden || modalOpen) return;
      const now = Date.now();
      if (now - visitTabLastRefreshedAt.current < 500) return;
      visitTabLastRefreshedAt.current = now;
      void refetchVisits();
    };
    const scheduleRefresh = () => {
      if (visitTabRefreshTimer.current) clearTimeout(visitTabRefreshTimer.current);
      visitTabRefreshTimer.current = setTimeout(() => {
        visitTabRefreshTimer.current = null;
        refreshAfterTabActivation();
      }, 100);
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) scheduleRefresh();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', scheduleRefresh);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', scheduleRefresh);
      if (visitTabRefreshTimer.current) clearTimeout(visitTabRefreshTimer.current);
    };
  }, [modalOpen, refetchVisits]);

  /** Copy all visits of a day to another day (header コピー). RA2: btn-copy-day → CopyModal */
  const openCopyDayModal = useCallback(
    (dayStr: string) => {
      if (hasLocalChangesRef.current) {
        appModal.warning({
          content: MSG.copy_error_unsaved,
        });
        return;
      }
      setCopyDayModalFromDate(dayStr);
    },
    [appModal]
  );

  const handleCopyDaySubmit = useCallback(
    async (copyToDate: string) => {
      if (copyDaySubmitLockRef.current) return;
      copyDaySubmitLockRef.current = true;
      try {
        const copyFrom = copyDayModalFromDate;
        if (!copyFrom) return;
        if (!copyToDate) {
          appModal.warning({
            content: MSG.copy_day_required,
          });
          return;
        }
        if (copyFrom === copyToDate) {
          appModal.warning({
            content: MSG.copy_day_same,
          });
          return;
        }
        if (!selectedRow || !isMassagerAvailableOnVisitDate(selectedRow, copyToDate)) {
          toast.warning(MSG.copy_massager_out_of_range);
          return;
        }
        const sourceVisits = calendarVisits.filter(
          v => dayjs(v.visit_datetime_from).format('YYYY-MM-DD') === copyFrom
        );
        if (sourceVisits.length === 0) {
          appModal.warning({
            content: MSG.copy_no_data,
          });
          return;
        }
        const hasDraft = sourceVisits.some(v => typeof v.id === 'string');
        if (hasDraft) {
          appModal.warning({
            content: MSG.copy_error_unsaved,
          });
          return;
        }
        const sourcePatientIds = Array.from(
          new Set(sourceVisits.map(v => v.patient_id).filter(id => id > 0))
        );
        try {
          // One request for every patient of the source day (was one per patient).
          const hasTreatmentOnTarget = await checkTreatmentBatchMutation(
            sourcePatientIds.map(patientId => ({
              patient_id: patientId,
              visit_dates: [copyToDate],
            }))
          );
          if (hasTreatmentOnTarget) {
            appModal.warning({
              content: MSG.copy_target_has_treatment,
            });
            return;
          }
        } catch {
          return;
        }
        const existingOnTarget = calendarVisits.filter(
          v => dayjs(v.visit_datetime_from).format('YYYY-MM-DD') === copyToDate
        );
        if (existingOnTarget.length > 0) {
          appModal.warning({
            content: MSG.copy_day_already_exist,
          });
          return;
        }
        try {
          const targetWeekResponse = await checkWeekMutation({
            massagerId: massagerId ?? 0,
            visitdate: copyToDate,
            clinicLocationId,
          });
          const targetWeekRaw = targetWeekResponse?.data;
          const targetWeekNested = targetWeekRaw as
            | { data?: VisitScheduleVisitDisplay[] }
            | undefined;
          const targetWeekVisits = Array.isArray(targetWeekRaw)
            ? targetWeekRaw
            : Array.isArray(targetWeekNested?.data)
              ? targetWeekNested.data
              : [];
          if (
            targetWeekVisits.some(
              v => dayjs(v.visit_datetime_from).format('YYYY-MM-DD') === copyToDate
            )
          ) {
            appModal.warning({
              content: MSG.copy_day_already_exist,
            });
            return;
          }
        } catch {
          return;
        }
        const hasSuddenlyVisitInCopySource = sourceVisits.some(v => Boolean(v.is_suddenly_visit));
        const toAdd: VisitScheduleVisitDisplay[] = sourceVisits.map(v => {
          const from = dayjs(v.visit_datetime_from);
          const to = dayjs(v.visit_datetime_to);
          const newFrom = dayjs(copyToDate)
            .hour(from.hour())
            .minute(from.minute())
            .second(0)
            .millisecond(0);
          const newTo = dayjs(copyToDate)
            .hour(to.hour())
            .minute(to.minute())
            .second(0)
            .millisecond(0);
          return {
            ...v,
            id: nextDraftId(),
            visit_datetime_from: newFrom.format('YYYY-MM-DDTHH:mm:ss'),
            visit_datetime_to: newTo.format('YYYY-MM-DDTHH:mm:ss'),
            patient_base_address1: undefined,
            patient_base_address2: undefined,
            patient_base_address3: undefined,
            patient_base_address_name: undefined,
            is_copied: true,
            copy_visit_id: typeof v.id === 'number' ? v.id : undefined,
            is_suddenly_visit: false,
            hospital_name: undefined,
            doctor_name: undefined,
            visit_reason: undefined,
            alignment_date: undefined,
          };
        });
        hasLocalChangesRef.current = true;
        markDatesDirty(copyFrom, copyToDate);
        setCalendarVisits(prev => [...prev, ...toAdd]);
        setCopyCheckFlg(true);
        setCopyDayModalFromDate(null);
        if (hasSuddenlyVisitInCopySource) {
          appModal.warning({
            content: MSG.copy_success_with_suddenly_visit,
          });
        } else {
          toast.success(MSG.copy_success);
        }
      } finally {
        copyDaySubmitLockRef.current = false;
      }
    },
    [
      copyDayModalFromDate,
      calendarVisits,
      massagerId,
      clinicLocationId,
      toast,
      appModal,
      selectedRow,
      checkWeekMutation,
      checkTreatmentBatchMutation,
    ]
  );

  /**
   * Delete visits for a day. Locked visits are kept; after deleting the unlocked
   * rows, show the rezept-issued warning required by the legacy flow.
   */
  const handleDeleteDayClick = useCallback(
    (dayStr: string) => {
      if (copyCheckFlg) {
        appModal.warning({
          content: MSG.delete_blocked_by_copy,
        });
        return;
      }
      const dayVisits = calendarVisits.filter(
        v => dayjs(v.visit_datetime_from).format('YYYY-MM-DD') === dayStr
      );
      const locked = dayVisits.filter(v => v.is_locked);
      const toRemoveIds = new Set(dayVisits.filter(v => !v.is_locked).map(v => v.id));

      const applyRemoveUnlocked = () => {
        if (toRemoveIds.size > 0) {
          hasLocalChangesRef.current = true;
          markDatesDirty(dayStr);
          const numericIds = [...toRemoveIds].filter((id): id is number => typeof id === 'number');
          setDeletedIds(prev => [...prev, ...numericIds]);
          setCalendarVisits(prev => prev.filter(v => !toRemoveIds.has(v.id)));
        }
      };

      const runStandardConfirm = () => {
        appModal.confirm({
          title: '確認',
          content: MSG.delete_confirm(dayStr),
          okText: '削除',
          okType: 'danger',
          cancelText: 'キャンセル',
          onOk: () => {
            applyRemoveUnlocked();
            if (locked.length > 0) {
              appModal.warning({
                title: '確認',
                content: MSG.delete_locked,
                okText: 'OK',
              });
            }
          },
        });
      };

      // UAT 2026-04-21: same-address head-count warning.
      // Visits sharing same_visit_id on a day form a 持ち回りグループ (same address).
      // group_count semantics mirror VisitScheduleService.php:1638-1639.
      const groupCounts = new Map<number, { backendCount: number; localCount: number; deleteCount: number }>();
      for (const v of dayVisits) {
        if (v.same_visit_id != null) {
          const current = groupCounts.get(v.same_visit_id) ?? {
            backendCount: 0,
            localCount: 0,
            deleteCount: 0,
          };
          current.backendCount = Math.max(current.backendCount, Number(v.group_count ?? 0));
          current.localCount += 1;
          if (!v.is_locked) {
            current.deleteCount += 1;
          }
          groupCounts.set(v.same_visit_id, current);
        }
      }
      const sameAddressGroups = [...groupCounts.values()]
        .map(group => ({
          before: Math.max(group.backendCount, group.localCount),
          deleteCount: group.deleteCount,
        }))
        .filter(group => group.before >= 2 && group.deleteCount > 0);

      if (sameAddressGroups.length > 0) {
        const before = sameAddressGroups.reduce((sum, group) => sum + group.before, 0);
        const after = sameAddressGroups.reduce(
          (sum, group) => sum + Math.max(group.before - group.deleteCount, 0),
          0
        );
        appModal.confirm({
          title: '確認',
          content: (
            <>
              {MSG.delete_same_address_warn}
              <br />
              {`削除前${before}人　→　削除後${after}人`}
            </>
          ),
          okText: 'OK',
          cancelText: 'キャンセル',
          onOk: runStandardConfirm,
        });
        return;
      }

      runStandardConfirm();
    },
    [copyCheckFlg, calendarVisits, appModal]
  );

  /** Detail for day (A2/R2 original opens a detail screen; RA2N uses in-page modal). */
  const handleDetailClick = useCallback(
    (dayStr: string) => {
      if (copyCheckFlg) {
        appModal.warning({
          content: MSG.edit_blocked_by_copy,
        });
        return;
      }
      const dayVisits = calendarVisits.filter(
        v => dayjs(v.visit_datetime_from).format('YYYY-MM-DD') === dayStr
      );
      if (dayVisits.length === 0) {
        appModal.warning({
          title: `訪問日 ${dayjs(dayStr).format('YYYY/MM/DD')}`,
          content: '訪問日に受診者情報の登録がありません。',
        });
        return;
      }
      setDetailModalDate(dayStr);
    },
    [copyCheckFlg, appModal, calendarVisits]
  );

  /** RA2: 次週にコピー — copy entire current week to next week (+7 days). Checkbox メモを含む = include massager_remarks. */
  const handleCopyWeek = useCallback(async () => {
    if (copyWeekSubmitLockRef.current) return;
    copyWeekSubmitLockRef.current = true;
    setIsCopyWeekSubmitting(true);
    try {
      if (hasLocalChangesRef.current) {
        appModal.warning({
          content: MSG.copy_error_unsaved,
        });
        return;
      }
      if (!massagerId) {
        appModal.warning({
          content: `${massagerLabel}を選択してください。`,
        });
        return;
      }
      const { start: weekStart, end: weekEnd } = getWeekRangeForVisitdate(visitdate);
      const weekStartStr = weekStart.format('YYYY-MM-DD');
      const weekEndStr = weekEnd.format('YYYY-MM-DD');

      const currentWeekVisits = calendarVisits.filter(v => {
        const d = dayjs(v.visit_datetime_from).format('YYYY-MM-DD');
        return d >= weekStartStr && d <= weekEndStr;
      });
      if (currentWeekVisits.length === 0) {
        appModal.warning({
          content: MSG.copy_no_data,
        });
        return;
      }
      const hasDraft = currentWeekVisits.some(v => typeof v.id === 'string');
      if (hasDraft) {
        appModal.warning({
          content: MSG.copy_error_unsaved,
        });
        return;
      }

      const nextWeekStart = weekStart.add(7, 'day');
      const nextWeekEnd = weekEnd.add(7, 'day');
      const nextWeekStartStr = nextWeekStart.format('YYYY-MM-DD');
      const nextWeekEndStr = nextWeekEnd.format('YYYY-MM-DD');

      // 施術師の適用期限チェック — loop through each day in the target week; if any day
      // falls outside the current CLM's available_from/available_to, block the copy.
      if (selectedRow) {
        let cursor = nextWeekStart.clone();
        while (cursor.format('YYYY-MM-DD') <= nextWeekEndStr) {
          if (!isMassagerAvailableOnVisitDate(selectedRow, cursor.format('YYYY-MM-DD'))) {
            toast.warning(MSG.copy_massager_out_of_range);
            return;
          }
          cursor = cursor.add(1, 'day');
        }
      }

      // 来院チェック for the whole target week in ONE request. Sending one request per
      // (patient, date) pair made this scale with the size of the week — 70 visits
      // meant 70 requests before the copy even started.
      const datesByPatient = new Map<number, Set<string>>();
      currentWeekVisits
        .filter(v => v.patient_id > 0)
        .forEach(v => {
          const targetDate = dayjs(v.visit_datetime_from).add(7, 'day').format('YYYY-MM-DD');
          const dates = datesByPatient.get(v.patient_id) ?? new Set<string>();
          dates.add(targetDate);
          datesByPatient.set(v.patient_id, dates);
        });
      const treatmentCheckPatients = Array.from(datesByPatient, ([patient_id, dates]) => ({
        patient_id,
        visit_dates: Array.from(dates),
      }));
      try {
        const hasTreatmentInTargetWeek = await checkTreatmentBatchMutation(treatmentCheckPatients);
        if (hasTreatmentInTargetWeek) {
          appModal.warning({
            content: MSG.copy_target_has_treatment,
          });
          return;
        }
      } catch {
        return;
      }

      const nextWeekVisitsInCalendar = calendarVisits.filter(v => {
        const d = dayjs(v.visit_datetime_from).format('YYYY-MM-DD');
        return d >= nextWeekStartStr && d <= nextWeekEndStr;
      });
      if (nextWeekVisitsInCalendar.length > 0) {
        appModal.warning({
          content: MSG.copy_week_next_week_has_data,
        });
        return;
      }

      // Backend check — Home Visit only (previous_visit_id <> -1), so Treatment
      // data in next week does not block the copy.
      let nextWeekHasData = false;
      try {
        nextWeekHasData = await checkWeekHasDataMutation({
          massagerId,
          visitdate: nextWeekStartStr,
          clinicLocationId,
        });
      } catch {
        return;
      }
      if (nextWeekHasData) {
        appModal.warning({
          content: MSG.copy_week_next_week_has_data,
        });
        return;
      }

      const includeMemo = copyWeekIncludeMemo;
      const hasSuddenlyVisitInCopySource = currentWeekVisits.some(v => Boolean(v.is_suddenly_visit));
      const toAdd: VisitScheduleVisitDisplay[] = currentWeekVisits.map(v => {
        const from = dayjs(v.visit_datetime_from).add(7, 'day');
        const to = dayjs(v.visit_datetime_to).add(7, 'day');
        return {
          ...v,
          id: nextDraftId(),
          visit_datetime_from: from.format('YYYY-MM-DDTHH:mm:ss'),
          visit_datetime_to: to.format('YYYY-MM-DDTHH:mm:ss'),
          patient_base_address1: undefined,
          patient_base_address2: undefined,
          patient_base_address3: undefined,
          patient_base_address_name: undefined,
          is_copied: true,
          copy_visit_id: typeof v.id === 'number' ? v.id : undefined,
          massager_remarks: includeMemo ? v.massager_remarks : undefined,
          is_suddenly_visit: false,
          hospital_name: undefined,
          doctor_name: undefined,
          visit_reason: undefined,
          alignment_date: undefined,
        };
      });
      hasLocalChangesRef.current = true;
      // Source week too: its rows are re-submitted as-is, and marking it keeps the
      // resolvers that run per day (持ち回り, number_of_people) covering both ends.
      markDatesDirty(...weekDatesOf(weekStartStr), ...weekDatesOf(nextWeekStartStr));
      setCalendarVisits(prev => [...prev, ...toAdd]);
      setCopyCheckFlg(true);
      if (hasSuddenlyVisitInCopySource) {
        appModal.warning({
          content: MSG.copy_success_with_suddenly_visit,
        });
      } else {
        toast.success(MSG.copy_success);
      }
      updateScheduleUrl(massagerId, clinicLocationId, nextWeekStartStr);
    } finally {
      copyWeekSubmitLockRef.current = false;
      setIsCopyWeekSubmitting(false);
    }
  }, [
    visitdate,
    calendarVisits,
    copyWeekIncludeMemo,
    toast,
    appModal,
    massagerId,
    clinicLocationId,
    massagerLabel,
    selectedRow,
    checkWeekHasDataMutation,
    checkTreatmentBatchMutation,
    updateScheduleUrl,
  ]);

  const handleSubmitTotal = useCallback(async () => {
    // Submit the FULL state of the days that changed, and nothing else. The calendar still
    // holds every week the user browsed, and sending all of it is what let a submit reach
    // back into untouched years.
    //
    // Locked rows on a changed day are deliberately included: the server treats the day's
    // payload as its complete new state, so a row left out of it is a row deleted.
    const dirtyDates = expandDirtyDatesByPatient(updateDates);
    const submittedVisits = calendarVisits.filter(v =>
      dirtyDates.has(dayjs(v.visit_datetime_from).format('YYYY-MM-DD'))
    );
    if (submittedVisits.length === 0 && deletedIds.length === 0 && !isFullMonth) {
      return true; // Nothing changed — 当月再更新 is the one reason to post an empty payload.
    }

    // 来院チェック for copied visits is NOT repeated here: submit-total already runs it
    // server-side (VisitScheduleService::assertCopiedVisitsDoNotOverlapTreatment, before
    // the transaction opens) and raises the very same message, which onError surfaces
    // through getApiErrorMessage. Re-checking on the client fired one request per copied
    // visit — a copied week paid for the whole check twice.

    // BD: pre-submit duplicate validation — calendarVisits is scoped to current massager,
    // so two rows for the same (patient_id, date) means the user added a duplicate draft.
    const sameMassagerKeys = new Set<string>();
    const hasSameMassagerDuplicate = submittedVisits.some(v => {
      if (!v.patient_id || v.patient_id <= 0) return false;
      const dateStr = dayjs(v.visit_datetime_from).format('YYYY-MM-DD');
      const key = `${v.patient_id}_${dateStr}`;
      if (sameMassagerKeys.has(key)) return true;
      sameMassagerKeys.add(key);
      return false;
    });
    if (hasSameMassagerDuplicate) {
      appModal.warning({
        content: MSG.submit_patient_already_same_massager,
      });
      return false;
    }

    const payload = {
      visits: submittedVisits.map(v => ({
        id: typeof v.id === 'number' ? v.id : undefined,
        patient_id: v.patient_id,
        massager_id: v.massager_id ?? 0,
        clinic_location_id: v.clinic_location_id ?? null,
        visit_datetime_from: v.visit_datetime_from,
        visit_datetime_to: v.visit_datetime_to,
        visit_minutes: v.visit_minutes,
        patient_address_name: v.patient_address_name ?? undefined,
        // null is sent as null: like legacy's empty value it means the 受診者情報 address.
        patient_address_id: v.patient_address_id,
        is_copied: v.is_copied ?? undefined,
        copy_visit_id: v.copy_visit_id ?? undefined,
        massager_remarks: v.massager_remarks ?? undefined,
        electric_treatment: v.electric_treatment,
        is_not_hot_treatment: v.is_not_hot_treatment,
        is_suddenly_visit: v.is_suddenly_visit ?? undefined,
        hospital_name: v.hospital_name ?? undefined,
        doctor_name: v.doctor_name ?? undefined,
        visit_reason: v.visit_reason ?? undefined,
        alignment_date: v.alignment_date ?? undefined,
      })),
      is_full_month: isFullMonth,
      visitdate,
      deleted_ids: deletedIds,
      // Only days whose whole week came back from the server may authorise a deletion.
      // A day edited through the modal's unrestricted 訪問日 picker without ever being
      // loaded is saved but never cleared — this browser does not know what else is there.
      update_dates: Array.from(dirtyDates).filter(d => loadedDates.includes(d)),
      massager_id: massagerId ?? undefined,
      clinic_location_id: clinicLocationId ?? undefined,
    };
    try {
      await submitTotalMutation(payload);
      // Wait for the invalidate-triggered query refresh before clearing local edits.
      // This keeps UI smooth without sending an extra manual refetch request.
      setIsAwaitingSubmitRefresh(true);
      return true;
    } catch {
      // 422 validation errors — toast already shown by mutation onError; keep local state
      // so the user can fix the offending visits without losing their draft.
      return false;
    }
  }, [
    calendarVisits,
    deletedIds,
    updateDates,
    loadedDates,
    expandDirtyDatesByPatient,
    massagerId,
    clinicLocationId,
    submitTotalMutation,
    isFullMonth,
    visitdate,
    appModal,
  ]);

  /**
   * RA2 parity (schedule.blade.php:268-275): switching 施術師 with unsaved edits prompts
   * 「現在のデータは編集されています。保存しますか？」
   *   OK     → save only (don't switch — matches RA2 btn-update flow)
   *   Cancel → discard local changes & switch to the new 施術師
   */
  const requestSwitchMassager = useCallback(
    (newRowId: number | null) => {
      // newRowId is the CLM id of the AppSelect entry (used only as a UI selection key);
      // we resolve it back to (massager_id, clinic_location_id) to build the canonical URL.
      const fallbackRow = massagersList[0] ?? null;
      const targetRow =
        newRowId !== null
          ? (massagersList.find(r => r.id === newRowId) ?? fallbackRow)
          : fallbackRow;
      const targetMassagerId = targetRow?.massager?.id ?? null;
      const targetClinicLocationId = targetRow?.clinic_location?.id ?? null;
      if (targetMassagerId === massagerId && targetClinicLocationId === clinicLocationId) return;
      if (!hasLocalChangesRef.current) {
        updateScheduleUrl(targetMassagerId, targetClinicLocationId, visitdate);
        return;
      }
      appModal.confirm({
        title: '現在のデータは編集されています。保存しますか？',
        okText: 'OK',
        cancelText: 'キャンセル',
        onOk: async () => {
          const ok = await handleSubmitTotal();
          if (ok) {
            updateScheduleUrl(targetMassagerId, targetClinicLocationId, visitdate);
          }
        },
        onCancel: () => {
          // Discarding the drafts must discard what they authorised too — otherwise the
          // next submit would clear those days for the massager the user just switched to.
          hasLocalChangesRef.current = false;
          setDeletedIds([]);
          setUpdateDates([]);
          updateScheduleUrl(targetMassagerId, targetClinicLocationId, visitdate);
        },
      });
    },
    [
      appModal,
      massagerId,
      clinicLocationId,
      handleSubmitTotal,
      massagersList,
      updateScheduleUrl,
      visitdate,
    ]
  );

  const massagerOptions = useMemo(() => buildMassagerOptions(massagersList), [massagersList]);

  // URL state guard:
  //   - empty list                         → clear URL params
  //   - URL massager unset                 → pick first row, build canonical 3-segment URL
  //   - URL massager set, location unset   → resolve via selectedRow, rewrite URL with location
  //   - URL pair resolves nothing          → fallback to first row
  //   - URL pair OK but resolved row drifts → re-write to canonical (defensive, usually no-op)
  useEffect(() => {
    if (loadingMassagers) return;

    if (massagersList.length === 0) {
      if (urlMassagerId !== null) {
        updateScheduleUrl(null, null, visitdate, true);
      }
      return;
    }

    if (urlMassagerId === null || selectedRow === null) {
      const first = massagersList[0];
      updateScheduleUrl(
        first.massager?.id ?? null,
        first.clinic_location?.id ?? null,
        visitdate,
        true
      );
      return;
    }

    // selectedRow OK; ensure URL carries the resolved location id (legacy 2-segment URL
    // arriving without location → rewrite once to canonical 3-segment).
    if (urlClinicLocationId === null && clinicLocationId !== null) {
      updateScheduleUrl(massagerId, clinicLocationId, visitdate, true);
    }
  }, [
    loadingMassagers,
    massagersList,
    urlMassagerId,
    urlClinicLocationId,
    selectedRow,
    massagerId,
    clinicLocationId,
    updateScheduleUrl,
    visitdate,
  ]);

  const isLoading = loadingMassagers || (!!massagerId && loadingVisits);

  const handlePrint = useCallback(() => {
    if (!massagerId) {
      appModal.warning({
        content: `${massagerLabel}を選択してください。`,
      });
      return;
    }

    const serviceTypeForPrint =
      serviceType === SERVICE_TYPE_A2 || serviceType === SERVICE_TYPE_R2 ? serviceType : undefined;
    triggerPrint({
      massager_id: massagerId,
      clinic_location_id: clinicLocationId ?? null,
      visitdate,
      service_type: serviceTypeForPrint,
    });
  }, [massagerId, clinicLocationId, massagerLabel, serviceType, appModal, triggerPrint, visitdate]);

  return (
    <div>
      <div className="space-y-2">
        <VisitScheduleToolbar
          massagerLabel={massagerLabel}
          massagerOptions={massagerOptions}
          selectedRowId={selectedRow?.id}
          visitdate={visitdate}
          massagerId={massagerId}
          isPrinting={isPrinting}
          onMassagerChange={requestSwitchMassager}
          onVisitDateChange={nextVisitdate =>
            updateScheduleUrl(massagerId, clinicLocationId, nextVisitdate)
          }
          onPrevWeek={goPrevWeek}
          onNextWeek={goNextWeek}
          onPrint={handlePrint}
        />

        {isLoading && <VisitScheduleLoading />}

        {selectedRow && !loadingMassagers && massagerId && !loadingVisits && (
          <VisitScheduleCalendarPanel
            visitdate={visitdate}
            visits={calendarVisits}
            checkinEnabled={checkinEnabled}
            isCheckinEnabledForVisit={isCheckinEnabledForVisit}
            departurePositionText={departurePositionText}
            copyWeekIncludeMemo={copyWeekIncludeMemo}
            copyWeekLoading={isCheckingNextWeek || isCopyWeekSubmitting}
            onCopyWeekIncludeMemoChange={setCopyWeekIncludeMemo}
            onCopyWeek={handleCopyWeek}
            onCellClick={handleCellClick}
            onVisitClick={handleVisitClick}
            onVisitDrop={handleVisitDrop}
            onVisitResize={handleVisitResize}
            onCopyDayClick={openCopyDayModal}
            onDeleteDayClick={handleDeleteDayClick}
            onDetailClick={handleDetailClick}
          />
        )}

        {!loadingMassagers && massagersList.length === 0 && (
          <VisitScheduleEmptyState isR2={isR2} />
        )}

        <VisitScheduleModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          visit={selectedVisit}
          initialDate={initialDate}
          initialTime={initialTime}
          onSave={handleSave}
          onDelete={handleDelete}
          onCopyToOtherDays={handleCopyToOtherDays}
          isSaving={false}
          existingEvents={calendarVisits}
          pendingDeletedVisitIds={deletedIds}
          currentMassagerId={massagerId}
          checkinEnabled={checkinEnabled}
          massagers={massagersList}
        />
        <VisitScheduleCopyDayModal
          isOpen={copyDayModalFromDate !== null}
          onClose={() => setCopyDayModalFromDate(null)}
          copyFromDate={copyDayModalFromDate}
          onSubmit={handleCopyDaySubmit}
        />
        <VisitScheduleDetailModal
          isOpen={detailModalDate !== null}
          onClose={() => setDetailModalDate(null)}
          detail={dayDetail}
          clinicLocationId={clinicLocationId}
          isLoading={loadingDayDetail}
        />
      </div>

      {/* Bottom action bar: データ更新 / 当月再更新 / note (RA2 btn_pannel) */}
      <VisitScheduleSubmitBar
        isFullMonth={isFullMonth}
        isSubmitting={isSubmitting}
        onFullMonthChange={setIsFullMonth}
        onSubmit={handleSubmitTotal}
      />
    </div>
  );
}
